import { Box, Button, Stack, Tooltip, Text, Grid, Divider, Group, TextInput } from '@mantine/core';
import { IconEdit, IconInfoCircle } from '@tabler/icons-react';
import { User, UserRole, UserStatus } from 'src/admin/users/types';
import { roleAsDisplayName, statusAsDisplayName } from 'src/admin/users/utils';
import { useEffect, useState } from 'react';
import TabHeader from './TabHeader';
import SignatureUpload from './SignatureUpload';
import { useUpdateUser } from 'src/admin/users/hooks/useUpdateUser';

type UserDetailsProps = {
  user: User | null;
  onUpdateSuccess: () => Promise<void>;
};

type FieldConfig = {
  key: keyof User;
  label: string;
  required?: boolean;
  withDivider?: boolean;
  editable?: boolean;
  displayTransform: (user: User) => React.ReactNode;
  editComponent?: (
    formState: User,
    handleChange: (key: keyof User, value: string | boolean | UserRole | UserStatus) => void,
  ) => React.ReactNode;
};

export default function UserDetails({ user, onUpdateSuccess }: UserDetailsProps) {
  const [editMode, setEditMode] = useState(false);
  const [formState, setFormState] = useState<User | null>(user);
  const { updateUser } = useUpdateUser();

  useEffect(() => {
    setFormState(user);
  }, [user]);

  const handleChange = (key: keyof User, value: string | boolean | UserRole | UserStatus) => {
    setFormState(prev => (prev ? { ...prev, [key]: value } : null));
  };

  if (!user || !formState) {
    return <Text c="dimmed">No user data available.</Text>;
  }

  const fieldConfigs: FieldConfig[] = [
    {
      key: 'firstName',
      label: 'Name',
      required: true,
      editable: true,
      displayTransform: user => `${user.firstName} ${user.lastName || ''}`,
      editComponent: (state, onChange) => (
        <Group grow>
          <TextInput
            value={state.firstName}
            onChange={e => onChange('firstName', e.currentTarget.value)}
            placeholder="First name"
          />
          <TextInput
            value={state.lastName}
            onChange={e => onChange('lastName', e.currentTarget.value)}
            placeholder="Last name"
          />
        </Group>
      ),
    },
    {
      key: 'emailAddress',
      label: 'Email address',
      editable: false,
      displayTransform: user => user.emailAddress,
    },
    {
      key: 'userRole',
      label: 'Job title',
      editable: false,
      displayTransform: user => roleAsDisplayName(user.userRole),
    },
    {
      key: 'userStatus',
      label: 'Status',
      withDivider: false,
      editable: false,
      displayTransform: user => statusAsDisplayName(user.userStatus),
    },
  ];

  const rows = fieldConfigs.map(config => ({
    key: config.label,
    label: config.label,
    required: config.required,
    withDivider: config.withDivider,
    value:
      editMode && config.editable && config.editComponent
        ? config.editComponent(formState, handleChange)
        : config.displayTransform(user),
  }));

  return (
    <Stack gap="xl">
      <ProfileInfoCard
        rows={rows}
        isEditing={editMode}
        onEdit={() => setEditMode(true)}
        onCancel={() => {
          setFormState(user);
          setEditMode(false);
        }}
        onSave={async () => {
          if (!formState) return;

          await updateUser(user.id, formState);
          await onUpdateSuccess();
          setEditMode(false);
        }}
      />

      <SignatureUpload />
    </Stack>
  );
}

function ProfileInfoCard({
  rows,
  isEditing,
  onEdit,
  onCancel,
  onSave,
}: {
  rows: InfoRowProps[];
  isEditing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
}) {
  const editSaveCancelButtons = (
    <Group gap="xs">
      <Button size="xs" variant="default" onClick={onCancel}>
        Cancel
      </Button>
      <Button size="xs" onClick={onSave}>
        Save
      </Button>
    </Group>
  );

  const editButton = (
    <Button size="xs" leftSection={<IconEdit size={14} />} onClick={onEdit}>
      Edit
    </Button>
  );

  return (
    <Stack>
      <TabHeader
        title="Personal info"
        subtitle={
          isEditing
            ? 'Make your changes and click save or cancel.'
            : 'Update your personal details here.'
        }
      >
        {isEditing ? editSaveCancelButtons : editButton}
      </TabHeader>

      <Divider size="xs" color="gray.2" mt="xs" />

      {rows.map(row => (
        <InfoRow {...row} key={row.label} />
      ))}
    </Stack>
  );
}

type InfoRowProps = {
  label: string;
  value: React.ReactNode;
  required?: boolean;
  tooltip?: string;
  withDivider?: boolean;
};

function InfoRow({ label, value, required = false, tooltip, withDivider = true }: InfoRowProps) {
  return (
    <Box>
      <Grid align="center" gap="sm">
        <Grid.Col span={4}>
          <Text size="sm" fw={500} component="span">
            {label}
            {required && (
              <Text component="span" c="red" fw={700} size="sm" ml={4}>
                *
              </Text>
            )}
            {tooltip && (
              <Tooltip label={tooltip} withArrow>
                <Box component="span" ml={4} display="inline-flex">
                  <IconInfoCircle size={14} />
                </Box>
              </Tooltip>
            )}
          </Text>
        </Grid.Col>

        <Grid.Col span={8}>
          <Text size="sm" c="dimmed">
            {value}
          </Text>
        </Grid.Col>
      </Grid>

      {withDivider && <Divider size="xs" color="gray.2" mt="xs" />}
    </Box>
  );
}
